import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Sparkles, Lock, Unlock, RotateCcw, Trash2 } from "lucide-react";
import { prettyTitle } from "@/lib/displayHelpers";
import { toast } from "sonner";

function daysLeftToRestore(deletedAt) {
  if (!deletedAt) return 0;
  const d = new Date(deletedAt);
  const ms = (d.getTime() + 30 * 86400000) - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

export default function Projects() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);

  const loadAll = () => {
    return Promise.all([
      api.get("/visualiser/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])),
      api.get("/visualiser/projects?include_deleted=true").then((r) => setDeleted(r.data)).catch(() => setDeleted([])),
    ]);
  };

  useEffect(() => {
    if (!user || user === false) return;
    loadAll().finally(() => setLoading(false));
  }, [user]);

  const restore = async (pid) => {
    try {
      await api.post(`/visualiser/projects/${pid}/restore`);
      toast.success("Project restored");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't restore project");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 lg:py-14" data-testid="projects-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-ink">Your projects</h1>
          <p className="text-brand-muted mt-1 text-sm">All your designs, saved automatically.</p>
          <p className="text-xs text-brand-muted mt-1" data-testid="projects-photo-hint">
            Each project is one property. Add up to 8 photos per project and design every angle.
          </p>
        </div>
        {/* Only show the top-right CTA once the customer has at least one
            project — empty state has its own primary "Start your first design"
            card centred on the page (avoids duplicate competing CTAs). */}
        {!loading && projects.length > 0 && (
          <Link to="/visualiser/new" data-testid="projects-new-btn"
            className="inline-flex items-center gap-2 bg-brand-navy text-white h-12 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 hover:shadow-navy transition-all tap-spring">
            <Plus size={16} /> Start another project
          </Link>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-3xl skeleton aspect-[3/4]" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-12 lg:p-16 text-center shadow-soft" data-testid="projects-empty"
        >
          <div className="w-20 h-20 mx-auto rounded-3xl bg-brand-teal/10 flex items-center justify-center mb-5">
            <Sparkles className="text-brand-teal" size={28} strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-brand-ink mb-2">Start your first design</h2>
          <p className="text-sm text-brand-muted mb-7 max-w-sm mx-auto">
            Snap a photo of any wall, room or exterior. Pick a colour. We&apos;ll handle the rest.
          </p>
          <Link to="/visualiser/new" data-testid="projects-empty-cta"
            className="inline-flex items-center gap-2 bg-brand-navy text-white h-12 px-6 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 transition-all tap-spring">
            Begin →
          </Link>
        </motion.div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="projects-grid">
          <Link
            to="/visualiser/new"
            data-testid="projects-grid-new-tile"
            className="group flex flex-col items-center justify-center aspect-[3/4] rounded-3xl border-2 border-dashed border-brand-teal/40 bg-brand-teal/5 text-brand-teal hover:bg-brand-teal/10 hover:border-brand-teal transition-all tap-spring"
          >
            <div className="w-14 h-14 rounded-2xl bg-white shadow-soft flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
              <Plus size={22} />
            </div>
            <div className="text-sm font-semibold">Start another project</div>
            <div className="text-[11px] text-brand-muted mt-1 px-4 text-center leading-snug">
              One property · up to 8 photos
            </div>
          </Link>
          {projects.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} onClick={() => nav(`/visualiser/p/${p.id}`)} />
          ))}
        </div>
      )}

      {/* Recently deleted — soft-deleted by the customer themselves, restorable for 30 days */}
      {!loading && deleted.length > 0 && (
        <div className="mt-12" data-testid="recently-deleted-section">
          <button
            onClick={() => setShowDeleted((v) => !v)}
            data-testid="recently-deleted-toggle"
            className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-ink"
          >
            <Trash2 size={14} />
            Recently deleted ({deleted.length}) {showDeleted ? "▴" : "▾"}
          </button>
          {showDeleted && (
            <div className="mt-4 space-y-2" data-testid="recently-deleted-list">
              {deleted.map((p) => {
                const days = daysLeftToRestore(p.deleted_at);
                return (
                  <div
                    key={p.id}
                    data-testid={`deleted-row-${p.id}`}
                    className="flex items-center justify-between gap-4 px-4 py-3 bg-white border border-brand-linen/60"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-brand-ink truncate">{prettyTitle(p.title)}</div>
                      <div className="text-xs text-brand-muted">
                        Deleted {new Date(p.deleted_at).toLocaleDateString("en-AU")}
                        {" · "}
                        {days > 0
                          ? `${days} day${days === 1 ? "" : "s"} left to restore`
                          : "Restore window closed — contact support"}
                      </div>
                    </div>
                    <button
                      data-testid={`deleted-restore-${p.id}`}
                      onClick={() => restore(p.id)}
                      disabled={days <= 0}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <RotateCcw size={12} /> Restore
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project, index, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoked = false; let url;
    api.get(`/visualiser/projects/${project.id}/photo`, { responseType: "blob" })
      .then((r) => {
        if (revoked) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      }).catch(() => {});
    return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
  }, [project.id]);

  return (
    <motion.button
      onClick={onClick}
      data-testid={`project-card-${project.id}`}
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -4 }}
      className="text-left bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-floating transition-shadow"
    >
      <div className="relative aspect-[3/4] bg-brand-sand">
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 skeleton" />
        )}
        <div className="absolute top-3 left-3">
          {project.unlocked ? (
            <span className="inline-flex items-center gap-1 bg-brand-navy text-white px-2.5 py-1 rounded-full text-[10px] font-semibold">
              <Unlock size={10} /> Unlocked
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur text-brand-ink px-2.5 py-1 rounded-full text-[10px] font-semibold">
              <Lock size={10} /> Preview
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-brand-ink truncate">{prettyTitle(project.title)}</h3>
        <p className="text-xs text-brand-muted mt-1">
          {(project.photo_count || 1)} photo{(project.photo_count || 1) === 1 ? "" : "s"}
          {" · "}
          {project.render_count} render{project.render_count === 1 ? "" : "s"}
          {" · "}
          {new Date(project.updated_at).toLocaleDateString()}
        </p>
      </div>
    </motion.button>
  );
}
